/* eslint-disable react-refresh/only-export-components -- co-locating Provider + hook for the edit-link context is intentional */
import { createContext, useContext, type ReactNode } from 'react';

import type { EditLinkSourceRef } from '@/components/modals/edit-link';

export type OpenEditLink = (
  ref: EditLinkSourceRef,
  mode: 'create' | 'edit',
) => void;

const EditLinkContext = createContext<OpenEditLink>(() => undefined);

export function EditLinkProvider({
  value,
  children,
}: {
  value: OpenEditLink;
  children: ReactNode;
}) {
  return (
    <EditLinkContext.Provider value={value}>
      {children}
    </EditLinkContext.Provider>
  );
}

export function useOpenEditLink(): OpenEditLink {
  return useContext(EditLinkContext);
}

/**
 * Exposes the source block id currently open in the EditLink modal (or
 * `null` when it's closed), so an EditorJS-backed block can pin itself
 * (`useViewportEditorActivation`'s `'modal'` reason) while it's the active
 * source — additive to `EditLinkProvider`/`useOpenEditLink` so existing
 * call sites are unaffected. Defaults to `null` when no provider is
 * mounted (e.g. read-only previews, tests), which simply means nothing
 * ever pins for the `'modal'` reason there.
 */
const EditLinkActiveSourceContext = createContext<string | null>(null);

export function EditLinkActiveSourceProvider({
  value,
  children,
}: {
  value: string | null;
  children: ReactNode;
}) {
  return (
    <EditLinkActiveSourceContext.Provider value={value}>
      {children}
    </EditLinkActiveSourceContext.Provider>
  );
}

export function useEditLinkActiveSourceId(): string | null {
  return useContext(EditLinkActiveSourceContext);
}
